// src/pages/purchase-requisition/history-pengajuan/components/HistoryOrderTable.jsx
import React, { useState } from 'react';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    IconButton, Chip, TablePagination, Box, Tooltip, Menu, MenuItem, Typography
} from "@mui/material";
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import MoreVertIcon from '@mui/icons-material/MoreVert';

import { formatDateTime, formatDate, formatTime, getDayName } from "../utils/formatters";
import { UserTooltipDisplay } from "../../PengajuanBarang";

/**
 * Renders the table displaying purchase order history.
 * @param {object} props - Component props.
 * @param {Array} props.orders - List of approved order data.
 * @param {Array} props.flowConfigurations - List of flow configurations.
 * @param {object} props.user - Authenticated user object.
 * @param {object} props.userProfile - User's profile data.
 * @param {string} props.prcnInvMaterialPackPHRoleId - Role ID for "Prcn. & Inv. Material Pack PH".
 * @param {string} props.invManagementFreshPineOtherRoleId - Role ID for "Inv Management FreshPine & Other".
 * @param {string} props.costAnalystFreshPineOtherRoleId - Role ID for "Cost Analyst Fresh Pine & Othrs".
 * @param {string} props.developerRoleId - Role ID for "Developer".
 * @param {function} props.handleViewOrder - Handler to view order details.
 * @param {function} props.handleDownloadExcel - Handler to download order data to Excel.
 * @param {function} props.formatTanggalCreat - Function to format tanggal creat for display.
 * @param {function} props.calculateInterval - Helper function to calculate approval interval.
 * @param {function} props.OrderIdDisplay - Component for displaying order ID with copy icon.
 */
const HistoryOrderTable = ({
    orders,
    userProfile,
    prcnInvMaterialPackPHRoleId,
    invManagementFreshPineOtherRoleId,
    costAnalystFreshPineOtherRoleId,
    developerRoleId,
    handleViewOrder,
    handleAddPr,
    handleDownloadExcel,
    formatTanggalCreat,
    calculateInterval,
    OrderIdDisplay
}) => {
    // State for pagination
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // State untuk tooltip copy
    const [copyTooltip, setCopyTooltip] = useState("Copy ID Order");
    
    // State untuk menu dropdown
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [menuOpen, setMenuOpen] = useState(false);

    // Handlers for pagination changes
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0); // Reset to the first page when rows per page changes
    };

    // Handler untuk copy ID Order
    const handleCopyId = (orderId) => {
        navigator.clipboard.writeText(orderId)
            .then(() => {
                setCopyTooltip("ID Order dicopy!");
                setTimeout(() => setCopyTooltip("Copy ID Order"), 2000);
            })
            .catch((err) => {
                console.error("Gagal menyalin ID Order:", err);
                setCopyTooltip("Gagal menyalin!");
                setTimeout(() => setCopyTooltip("Copy ID Order"), 2000);
            });
    };

    // Handler untuk membuka menu dropdown
    const handleMenuOpen = (event, order) => {
        setAnchorEl(event.currentTarget);
        setSelectedOrder(order);
        setMenuOpen(true);
    };

    // Handler untuk menutup menu dropdown
    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedOrder(null);
        setMenuOpen(false);
    };

    // Handler untuk memilih item dari menu dropdown
    const handleMenuAction = (action) => {
        if (!selectedOrder) return;
        
        switch (action) {
            case 'view':
                handleViewOrder(selectedOrder);
                break;
            case 'edit':
                handleAddPr(selectedOrder);
                break;
            case 'download':
                handleDownloadExcel(selectedOrder);
                break;
            default:
                break;
        }
        handleMenuClose();
    };

    // Helper function to get the final approval date and time
    const getFinalApprovalInfo = (order) => {
        if (!order.approval_history || !Array.isArray(order.approval_history)) {
            return { date: '-', day: '-', time: '-', fullDate: null };
        }
        
        // Find the last approval entry
        const finalApproval = order.approval_history
            .filter(entry => entry.decision === "Approved")
            .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))[0];
        
        if (!finalApproval || !finalApproval.timestamp) {
            return { date: '-', day: '-', time: '-', fullDate: null };
        }
        
        const approvalDate = new Date(finalApproval.timestamp);
        
        return {
            date: formatDate(approvalDate, 'DD/MM/YYYY'),
            day: getDayName(approvalDate),
            time: formatTime(approvalDate, 'HH.mm.ss'),
            fullDate: approvalDate
        };
    };

    // Helper function to get tanggal creat date and time info
    const getTanggalCreatInfo = (order) => {
        if (!order.tanggal_creat) {
            return { date: '-', day: '-', time: '-', fullDate: null };
        }
        
        try {
            // Coba parsing tanggal_creat
            let tanggalCreatDate;
            
            // Jika tanggal_creat sudah dalam format Date object
            if (order.tanggal_creat instanceof Date) {
                tanggalCreatDate = order.tanggal_creat;
            } else {
                // Coba parsing dari string ISO (YYYY-MM-DDTHH:mm:ss) atau format lain
                tanggalCreatDate = new Date(order.tanggal_creat);
                
                // Jika parsing gagal, coba parsing dengan dayjs
                if (isNaN(tanggalCreatDate.getTime())) {
                    const date = new Date(order.tanggal_creat);
                    if (!isNaN(date.getTime())) {
                        tanggalCreatDate = date;
                    } else {
                        // Coba format YYYY-MM-DD atau YYYY-MM-DDTHH:mm:ss
                        const dateStr = order.tanggal_creat;
                        if (dateStr.includes('T')) {
                            // ISO format dengan waktu
                            const [datePart, timePart] = dateStr.split('T');
                            if (datePart) {
                                const [year, month, day] = datePart.split('-').map(Number);
                                tanggalCreatDate = new Date(year, month - 1, day);
                            }
                        } else if (dateStr.includes('-')) {
                            // Format YYYY-MM-DD tanpa waktu
                            const [year, month, day] = dateStr.split('-').map(Number);
                            tanggalCreatDate = new Date(year, month - 1, day);
                        }
                    }
                }
            }
            
            if (isNaN(tanggalCreatDate.getTime())) {
                return { date: '-', day: '-', time: '-', fullDate: null };
            }
            
            // Format waktu berdasarkan data yang tersimpan
            let timeString = '00:00:00';
            
            // Cek apakah ada informasi waktu dalam tanggal_creat
            if (order.tanggal_creat.includes('T')) {
                // Jika ada informasi waktu dalam ISO format
                const timePart = order.tanggal_creat.split('T')[1];
                if (timePart) {
                    // Hapus bagian milidetik jika ada
                    const timeWithoutMillis = timePart.split('.')[0];
                    const timeParts = timeWithoutMillis.split(':');
                    if (timeParts.length >= 2) {
                        const hours = timeParts[0].padStart(2, '0');
                        const minutes = timeParts[1].padStart(2, '0');
                        const seconds = timeParts[2] ? timeParts[2].padStart(2, '0') : '00';
                        timeString = `${hours}:${minutes}:${seconds}`;
                    }
                }
            }
            
            return {
                date: formatDate(tanggalCreatDate, 'DD/MM/YYYY'),
                day: getDayName(tanggalCreatDate),
                time: timeString,
                fullDate: tanggalCreatDate
            };
        } catch (error) {
            console.error("Error parsing tanggal creat:", error);
            return { date: '-', day: '-', time: '-', fullDate: null };
        }
    };

    // Slice the orders array for current page display
    const paginatedOrders = orders.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    // Check if user has edit permissions
    const userRoles = userProfile?.id_role ? userProfile.id_role.split(';') : [];
    
    // Check if user has edit permissions
    const canEditOrders = userRoles.includes(prcnInvMaterialPackPHRoleId) || 
                         userRoles.includes(invManagementFreshPineOtherRoleId) ||
                         userRoles.includes(developerRoleId);

    // Check if user has download permissions (Developer, Inv Management FreshPine & Other, Prcn. & Inv. Material Pack PH, Cost Analyst Fresh Pine & Othrs)
    const canDownloadOrders = userRoles.includes(developerRoleId) || 
                             userRoles.includes(invManagementFreshPineOtherRoleId) || 
                             userRoles.includes(prcnInvMaterialPackPHRoleId) || 
                             userRoles.includes(costAnalystFreshPineOtherRoleId);

    return (
        <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader>
                <TableHead>
                    <TableRow>
                        <TableCell>ID</TableCell>
                        <TableCell>Order Title</TableCell>
                        <TableCell>Purchase Request No</TableCell>
                        <TableCell>Requested By</TableCell>

                        <TableCell>PR Created Date</TableCell>
                        <TableCell>Approved Date</TableCell>
                        <TableCell>Interval</TableCell>
                        
                        <TableCell>Total Items</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Actions</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {paginatedOrders.length > 0 ? (
                        paginatedOrders.map((order) => {
                            // Tentukan apakah No PR sudah ada
                            const hasPrNumber = order.no_pr && order.no_pr.trim() !== '';
                            const approvalInfo = getFinalApprovalInfo(order);
                            const tanggalCreatInfo = getTanggalCreatInfo(order);

                            return (
                                <TableRow key={order.id}>
                                    {/* ID */}
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Tooltip title={copyTooltip} placement="top">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleCopyId(order.id)}
                                                    sx={{ 
                                                        padding: '4px',
                                                        '&:hover': {
                                                            backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                                        }
                                                    }}
                                                >
                                                    <ContentCopyIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </TableCell>

                                    {/* Order Title */}
                                    <TableCell>
                                        {order.order_title || '-'}
                                    </TableCell>

                                    {/* Purchase Request No */}
                                    <TableCell>{order.no_pr || '-'}</TableCell>

                                    {/* Requested By */}
                                    <TableCell>
                                        <UserTooltipDisplay
                                            uid={order.created_by_uid}
                                            userName={order.created_by_name}
                                        />
                                    </TableCell>

                                    {/* PR Created Date */}
                                    <TableCell>
                                        <Tooltip 
                                            title={
                                                <Box sx={{ p: 1 }}>
                                                    <Typography variant="body2"><strong>Hari:</strong> {tanggalCreatInfo.day}</Typography>
                                                    <Typography variant="body2"><strong>Waktu:</strong> {tanggalCreatInfo.time}</Typography>
                                                </Box>
                                            }
                                            placement="bottom"
                                        >
                                            <span style={{ cursor: 'pointer' }}>
                                                {formatTanggalCreat(order.tanggal_creat)}
                                            </span>
                                        </Tooltip>
                                    </TableCell>

                                    {/* Approved Date */}
                                    <TableCell>
                                        <Tooltip 
                                            title={
                                                <Box sx={{ p: 1 }}>
                                                    <Typography variant="body2"><strong>Hari:</strong> {approvalInfo.day}</Typography>
                                                    <Typography variant="body2"><strong>Waktu:</strong> {approvalInfo.time}</Typography>
                                                </Box>
                                            }
                                            placement="bottom"
                                        >
                                            <span style={{ cursor: 'pointer' }}>
                                                {approvalInfo.date}
                                            </span>
                                        </Tooltip>
                                    </TableCell>

                                    {/* Interval */}
                                    <TableCell>{calculateInterval(order)}</TableCell>

                                    {/* Total Items */}
                                    <TableCell>{order.item_details?.length || 0} items</TableCell>
                                    
                                    {/* Status */}
                                    <TableCell>
                                        <Chip
                                            label={order.status}
                                            color="success"
                                            size="small"
                                        />
                                    </TableCell>

                                    {/* Actions */}
                                    <TableCell>
                                        <IconButton 
                                            size="small" 
                                            onClick={(e) => handleMenuOpen(e, order)}
                                        >
                                            <MoreVertIcon />
                                        </IconButton>
                                    </TableCell>

                                </TableRow>
                            );
                        })
                    ) : (
                        <TableRow>
                            <TableCell colSpan={10} align="center">Tidak ada history pengajuan.</TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
            
            {/* Menu Dropdown untuk Aksi */}
            <Menu
                anchorEl={anchorEl}
                open={menuOpen}
                onClose={handleMenuClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <MenuItem onClick={() => handleMenuAction('view')}>
                    <VisibilityOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
                    Detail Order
                </MenuItem>
                
                {canEditOrders && selectedOrder && (
                    <MenuItem onClick={() => handleMenuAction('edit')}>
                        {selectedOrder.no_pr && selectedOrder.no_pr.trim() !== '' ? (
                            <>
                                <EditIcon fontSize="small" sx={{ mr: 1 }} />
                                Edit
                            </>
                        ) : (
                            <>
                                <NoteAddIcon fontSize="small" sx={{ mr: 1 }} />
                                Tambahkan No PR
                            </>
                        )}
                    </MenuItem>
                )}
                
                {canDownloadOrders && selectedOrder && (
                    <MenuItem onClick={() => handleMenuAction('download')}>
                        <FileDownloadIcon fontSize="small" sx={{ mr: 1 }} />
                        Download
                    </MenuItem>
                )}
            </Menu>
            
            <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={orders.length} // Total number of items
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Baris per halaman:"
                labelDisplayedRows={({ from, to, count }) =>
                    `${from}-${to} dari ${count !== -1 ? count : `lebih dari ${to}`}`
                }
            />
        </TableContainer>
    );
};

export default HistoryOrderTable;